import { PropsWithChildren, ReactNode } from "react";

import { IconNavigationCross } from "@/assets/icons/figma/IconNavigationCross";
import { cn } from "@/common/utils/css.utils";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@radix-ui/react-dialog";

/**
 * The design system's Modal, as documented in Notion 09.2 · Components → Modal.
 *
 * Values are taken from there rather than invented: 10px radius, shadow-xl, a 40% black scrim, and
 * a dialog centred on both desktop and mobile (not a bottom sheet). Padding is 24px on desktop and
 * 16px on mobile; mobile width is the viewport minus an 8px margin each side — that 8px is a
 * dialog-specific value, *not* the general 16px mobile edge padding, so don't "fix" it to match.
 * Desktop has no fixed width: a soft 384px minimum, hugging its content up to ~512px.
 *
 * The header keeps icon and title together in the header row. The live save-search modal puts them
 * inside the content area with a negative-margin hack — documented as a dev oversight, and the
 * reason this shell exists rather than another bespoke dialog.
 *
 * Feature-local on purpose: it is the system component but only one feature uses it so far. Promote
 * it to `src/ui/cva` when a second consumer turns up, rather than pre-emptively.
 */
type SearchDialogProps = PropsWithChildren<{
  title: string;
  /** 32×32, tinted by the caller — the system has no fixed icon per message. */
  icon?: ReactNode;
  /** Optional message under the title. Always Body, centred, black — never restyled per instance. */
  copy?: string;
  /**
   * Buttons, **primary first**. The footer reverses on desktop, which is what puts the primary on
   * the right there and on top when they stack on mobile — both straight out of Button's combining
   * rules. Never pass two of the same style (Fill + Fill); pair Fill with Outline or Minimal.
   */
  footer?: ReactNode;
  closeLabel: string;
}>;

export const SearchDialog = ({
  title,
  icon,
  copy,
  footer,
  closeLabel,
  children,
}: SearchDialogProps) => (
  <DialogPortal>
    <DialogOverlay className="fixed inset-0 z-[1000] bg-black/40 data-[state=closed]:animate-dialog-overlay-hide data-[state=open]:animate-dialog-overlay-show" />
    <DialogContent
      className={cn(
        "fixed left-1/2 top-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2",
        "w-[calc(100vw-16px)] md:w-auto md:min-w-96 md:max-w-lg",
        "rounded-[10px] bg-white p-4 shadow-xl md:p-6"
      )}
      aria-describedby={copy ? undefined : ""}
    >
      <div className="flex items-start space-x-2">
        {/* Mirrors the close button, so the title stays centred whatever its length. */}
        <div className="h-8 w-8 shrink-0" aria-hidden />

        <div className="flex min-w-0 flex-1 flex-col items-center space-y-2">
          {icon}
          <DialogTitle className="text-center font-title text-lg font-bold leading-7 text-black">
            {title}
          </DialogTitle>
        </div>

        <DialogClose
          aria-label={closeLabel}
          className="h-8 w-8 shrink-0 rounded-full p-1 text-black hover:bg-attention-100 hover:text-attention-500"
        >
          <IconNavigationCross className="h-6 w-6" />
        </DialogClose>
      </div>

      {copy && (
        <DialogDescription className="pt-2 text-center font-body text-base leading-6 text-black">
          {copy}
        </DialogDescription>
      )}

      {children && <div className="pt-4">{children}</div>}

      {footer && (
        /* Full-width and stacked on mobile; on desktop the row splits evenly, so a pair of CTAs
         * reads as one decision rather than two buttons of whatever width their labels happen to
         * need — which also keeps the split stable across six languages.
         *
         * `space-x-reverse` because the row is reversed: it moves the margin to the right-hand
         * side, which is the one that separates the two buttons there. */
        <div className="flex flex-col space-y-2 pt-6 md:flex-row-reverse md:space-x-2 md:space-y-0 md:space-x-reverse md:[&>*]:flex-1">
          {footer}
        </div>
      )}
    </DialogContent>
  </DialogPortal>
);
